/**
 * DocumentUpload.jsx
 *
 * File upload component for documents (PDF, Excel, Word, CSV, TXT).
 * Documents are uploaded via presigned URLs and their text is extracted
 * server-side for LLM analysis.
 */

import React, { useRef, useState, useCallback } from 'react'

const ALLOWED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'text/csv',
  'text/plain',
]

const TYPE_LABELS = {
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'application/vnd.ms-excel': 'Excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/msword': 'Word',
  'text/csv': 'CSV',
  'text/plain': 'Text',
}

const MAX_SIZE = 50 * 1024 * 1024 // 50MB
const MAX_DOCUMENTS = 5

function DocumentUpload({ documents, onDocumentsChange }) {
  const [error, setError] = useState(null)
  const inputRef = useRef(null)

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Unsupported file type. Please upload PDF, Excel, Word, CSV, or text files.'
    }
    if (file.size > MAX_SIZE) {
      return 'File too large. Maximum size is 50MB.'
    }
    return null
  }

  const handleFiles = useCallback((files) => {
    setError(null)
    const newDocs = []

    for (const file of files) {
      if (documents.length + newDocs.length >= MAX_DOCUMENTS) {
        setError(`Maximum ${MAX_DOCUMENTS} documents allowed.`)
        break
      }

      const validationError = validateFile(file)
      if (validationError) {
        setError(validationError)
        continue
      }

      newDocs.push(file)
    }

    if (newDocs.length > 0) {
      onDocumentsChange([...documents, ...newDocs])
    }
  }, [documents, onDocumentsChange])

  const handleClick = () => {
    inputRef.current?.click()
  }

  const handleInputChange = (e) => {
    handleFiles(Array.from(e.target.files))
    e.target.value = ''
  }

  const removeDocument = (index) => {
    const newDocs = [...documents]
    newDocs.splice(index, 1)
    onDocumentsChange(newDocs)
  }

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={handleClick}
        className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Attach document
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          multiple
          onChange={handleInputChange}
          className="hidden"
        />
      </button>

      <p className="text-xs text-gray-500">
        PDF, Excel, Word, CSV, or text files up to 50MB ({documents.length}/{MAX_DOCUMENTS})
      </p>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {documents.length > 0 && (
        <ul className="space-y-2">
          {documents.map((doc, index) => (
            <li key={index} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                {TYPE_LABELS[doc.type] || 'File'}
              </span>
              <span className="flex-1 text-sm text-gray-700 truncate">{doc.name}</span>
              <span className="text-xs text-gray-400">{formatSize(doc.size)}</span>
              <button
                type="button"
                onClick={() => removeDocument(index)}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default DocumentUpload
